import React, { useState } from 'react';
import { Job, RiskAssessment as RiskAssessmentType } from '../types';
import { Icons } from '../constants';

interface Props {
  job: Job;
  onComplete: (assessment: Omit<RiskAssessmentType, 'id' | 'createdAt'>) => void;
  onClose: () => void;
}

const RiskAssessment: React.FC<Props> = ({ job, onComplete, onClose }) => {
  const [assessor, setAssessor] = useState('');
  const [weather, setWeather] = useState('');
  const [ground, setGround] = useState('');
  const [ppe, setPpe] = useState({
    helmet: false,
    eyeProtection: false,
    earProtection: false,
    chainsawChaps: false,
    highViz: false,
    gloves: false,
    climbingGear: false
  });
  const [risks, setRisks] = useState('');
  const [mitigation, setMitigation] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete({
      jobId: job.id,
      date: new Date().toISOString(),
      assessorName: assessor,
      weatherConditions: weather,
      groundConditions: ground,
      ppeRequirements: Object.entries(ppe).filter(([_, v]) => v).map(([k]) => k),
      identifiedRisks: risks.split('\n').filter(Boolean),
      mitigationSteps: mitigation.split('\n').filter(Boolean)
    });
  };

  return (
    <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="bg-orange-500 p-6 text-white flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            <Icons.Warning className="w-6 h-6" />
            <h2 className="text-xl font-black uppercase tracking-widest">Risk Assessment</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition">
            <Icons.Close className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Assessor Name</label>
              <input 
                required
                type="text"
                className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-orange-500 font-medium text-bark"
                value={assessor}
                onChange={e => setAssessor(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Weather Conditions</label>
              <input 
                required
                type="text"
                placeholder="e.g. Clear, 15mph wind"
                className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-orange-500 font-medium text-bark"
                value={weather}
                onChange={e => setWeather(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Ground Conditions</label>
              <input 
                required
                type="text"
                placeholder="e.g. Dry, sloped"
                className="w-full p-3 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-orange-500 font-medium text-bark"
                value={ground}
                onChange={e => setGround(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">Required PPE</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.keys(ppe).map(key => (
                <label key={key} className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition ${ppe[key as keyof typeof ppe] ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-stone-200 text-stone-500 hover:border-stone-300'}`}>
                  <input 
                    type="checkbox" 
                    className="hidden"
                    checked={ppe[key as keyof typeof ppe]}
                    onChange={e => setPpe({...ppe, [key]: e.target.checked})}
                  />
                  <span className="font-bold text-sm capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Identified Risks (One per line)</label>
              <textarea 
                required
                rows={4}
                placeholder="e.g. Overhead power lines&#10;Uneven terrain"
                className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-orange-500 font-medium text-bark"
                value={risks}
                onChange={e => setRisks(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Mitigation Steps (One per line)</label>
              <textarea 
                required
                rows={4}
                placeholder="e.g. Maintain 10ft clearance&#10;Use spotter"
                className="w-full p-4 bg-stone-50 rounded-xl border-none focus:ring-2 focus:ring-orange-500 font-medium text-bark"
                value={mitigation}
                onChange={e => setMitigation(e.target.value)}
              />
            </div>
          </div>

          <div className="pt-4">
            <button type="submit" className="w-full py-4 bg-orange-500 text-white rounded-xl font-black uppercase tracking-widest hover:bg-orange-600 transition shadow-lg shadow-orange-500/20">
              Submit Risk Assessment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RiskAssessment;
